import { useContext, useState } from "react";
import { cn } from "@/shared/lib/utils";
import type {
  AttributeField,
  AreaField,
  InputField,
  MultiSelectField,
  PhotosField as PhotosFieldType,
  SelectField as SelectFieldType,
} from "@/features/properties/types/propertyAttributes";
import { getOptionsForTitle } from "../hooks/useTemplateOptions";
import { QuestionMetadataRow } from "../metadata/QuestionMetadataRow";
import { OptionsMapContext, RequiredSetContext, SurveyIdContext } from "./contexts";
import {
  AreaFieldEditor,
  BooleanFieldEditor,
  MultiSelectFieldEditor,
  NumberFieldEditor,
  PhotosFieldView,
  SelectFieldEditor,
  TextFieldEditor,
} from "./editors";

export type LeafField = InputField | SelectFieldType | MultiSelectField | PhotosFieldType | AreaField;

export function LeafFieldRenderer({
  field,
  propertyId,
  nodePath = [],
  onUpdateField,
  onUpdateFieldMetadata,
  onOpenPhotos,
}: {
  field: LeafField;
  propertyId: string;
  /** Ids of ancestor node sections, outermost first; used for upload paths. */
  nodePath?: string[];
  onUpdateField: (fieldId: string, value: string | number | boolean | string[] | null) => void;
  onUpdateFieldMetadata: (
    fieldId: string,
    patch: { confidence?: number | null; evidence?: string[]; notes?: string | null }
  ) => void;
  onOpenPhotos: (urls: string[], index: number, title: string) => void;
}) {
  const templateOptionsMap = useContext(OptionsMapContext);
  const requiredSet = useContext(RequiredSetContext);
  const surveyId = useContext(SurveyIdContext);
  const cleanTitle = field.title.replace(/\s*\[.*?\]\s*$/, "");
  const [metaOpen, setMetaOpen] = useState(false);

  const toggleMeta = () => setMetaOpen((o) => !o);

  // Toggle the details row when the user clicks anywhere in the editor row that
  // isn't an interactive control. Labels are NOT excluded — the field editors
  // wrap the title text in <Label> with no htmlFor binding, so clicking the
  // title is the most natural place to expand details.
  const handleEditorClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (
      target.closest(
        'input, textarea, select, button, a, [role="combobox"], [role="slider"], [role="switch"], [role="checkbox"], [contenteditable="true"]'
      )
    ) {
      return;
    }
    toggleMeta();
  };

  let editor: JSX.Element;

  if (field.type === "photos") {
    editor = (
      <PhotosFieldView
        title={cleanTitle}
        urls={field.value}
        propertyId={propertyId}
        surveyId={surveyId}
        nodePath={nodePath}
        refId={field.id}
        onUpdate={onUpdateField}
        onOpenPhotos={onOpenPhotos}
      />
    );
  } else if (field.type === "area") {
    editor = (
      <AreaFieldEditor
        title={cleanTitle}
        value={field.value}
        refId={field.id}
        onUpdate={onUpdateField}
      />
    );
  } else if (field.type === "multiSelect") {
    const options = (field as { options?: string[] | null }).options ?? getOptionsForTitle(templateOptionsMap, field.title);
    editor = (
      <MultiSelectFieldEditor
        title={cleanTitle}
        values={field.value}
        options={options || []}
        refId={field.id}
        onUpdate={onUpdateField}
      />
    );
  } else if (field.type === "select") {
    const options = (field as { options?: string[] | null }).options ?? getOptionsForTitle(templateOptionsMap, field.title);
    if (options) {
      editor = (
        <SelectFieldEditor
          title={cleanTitle}
          value={field.value != null ? String(field.value) : null}
          options={options}
          refId={field.id}
          onUpdate={onUpdateField}
        />
      );
    } else if (typeof field.value === "boolean") {
      editor = (
        <BooleanFieldEditor
          title={cleanTitle}
          value={field.value}
          refId={field.id}
          onUpdate={onUpdateField}
        />
      );
    } else if (typeof field.value === "number") {
      editor = (
        <NumberFieldEditor
          title={cleanTitle}
          value={field.value}
          refId={field.id}
          onUpdate={onUpdateField}
        />
      );
    } else {
      editor = (
        <SelectFieldEditor
          title={cleanTitle}
          value={field.value as string | null}
          options={[]}
          refId={field.id}
          onUpdate={onUpdateField}
        />
      );
    }
  } else if (typeof field.value === "boolean") {
    editor = (
      <BooleanFieldEditor
        title={cleanTitle}
        value={field.value}
        refId={field.id}
        onUpdate={onUpdateField}
      />
    );
  } else if (typeof field.value === "number") {
    editor = (
      <NumberFieldEditor
        title={cleanTitle}
        value={field.value}
        refId={field.id}
        onUpdate={onUpdateField}
      />
    );
  } else {
    editor = (
      <TextFieldEditor
        title={cleanTitle}
        value={field.value as string | null}
        refId={field.id}
        onUpdate={onUpdateField}
      />
    );
  }

  const isRequired = requiredSet.has(cleanTitle.toLowerCase());

  return (
    <div className={cn(
      "rounded -mx-2 px-2 hover:bg-accent/20 transition-colors",
      isRequired && "border-l-2 border-l-primary"
    )}>
      <div onClick={handleEditorClick} className="cursor-pointer">
        {editor}
      </div>
      <QuestionMetadataRow
        fieldId={field.id}
        propertyId={propertyId}
        surveyId={surveyId}
        nodePath={nodePath}
        confidence={(field as any).confidence}
        evidence={(field as any).evidence}
        notes={(field as any).notes}
        open={metaOpen}
        onToggle={toggleMeta}
        onChange={(patch) => onUpdateFieldMetadata(field.id, patch)}
        onOpenPhotos={onOpenPhotos}
      />
    </div>
  );
}

// Re-exported so FieldRenderer (which lives next door) can import them together
export type { AttributeField };
